// data-attributes の解析と初期化フロー
import { ref } from 'vue';

export const useDataSource = () => {
  const items = ref([]);
  const folders = ref([]);
  const allowedFolderIds = ref(null);
  const selectedFolderId = ref(null);
  const searchScope = ref('folder');
  const hasSearchConditions = ref(false);
  const itemsTotal = ref(0);
  const folderSearchCount = ref(0);
  const focusQuery = ref('');
  const activeKeyword = ref('');
  const currentUserId = ref('');
  const canUseFulltextSearch = ref(false);
  const folderCountLabelTemplate = ref('');
  const fileCountLabelTemplate = ref('');
  const maxInputVars = ref(0);
  const bulkMaxItems = ref(0);
  const restoreCounts = ref({
    global: {
      restoring: { count: 0, display_ids: [], more: 0 },
      restored: { count: 0, display_ids: [], more: 0 },
    },
    current: {
      restoring: { count: 0, display_ids: [], more: 0 },
      restored: { count: 0, display_ids: [], more: 0 },
    },
  });

  const parseJsonAttribute = (element, attribute, fallback = null) => {
    if (!element || !element.hasAttribute(attribute)) {
      return fallback;
    }
    const raw = element.getAttribute(attribute);
    if (raw === null || raw === '') {
      return fallback;
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  };

  const resolveDataSource = () =>
    document.getElementById('dashboard-data-source') ||
    document.querySelector('[data-items][data-view-mode]') ||
    document.getElementById('app');

  const initDataSource = () => {
    const dataSource = resolveDataSource();

    const extractItems = () => {
      const parsed = parseJsonAttribute(dataSource, 'data-items', []);
      return Array.isArray(parsed) ? parsed : [];
    };

    const extractFolders = () => {
      const parsed = parseJsonAttribute(dataSource, 'data-folders', []);
      return Array.isArray(parsed) ? parsed : [];
    };

    items.value = extractItems();
    folders.value = extractFolders();
    const rawItemsTotal = Number(dataSource?.getAttribute('data-items-total'));
    itemsTotal.value =
      Number.isFinite(rawItemsTotal) && rawItemsTotal >= 0
        ? Math.trunc(rawItemsTotal)
        : items.value.length;

    const initialAllowedFolderIds = parseJsonAttribute(
      dataSource,
      'data-allowed-folder-ids'
    );
    if (initialAllowedFolderIds !== null) {
      allowedFolderIds.value = initialAllowedFolderIds;
    }

    const initialFolderId = parseJsonAttribute(dataSource, 'data-selected-folder-id');
    if (initialFolderId !== null) {
      selectedFolderId.value = initialFolderId;
    }

    searchScope.value = dataSource?.getAttribute('data-search-scope') || 'folder';
    hasSearchConditions.value = dataSource?.getAttribute('data-has-search-conditions') === '1';

    const rawFolderSearchCount = Number(dataSource?.getAttribute('data-folder-search-count'));
    folderSearchCount.value = Number.isFinite(rawFolderSearchCount) && rawFolderSearchCount >= 0
        ? Math.trunc(rawFolderSearchCount)
        : 0;

    focusQuery.value = dataSource?.getAttribute('data-focus-query') || '';

    activeKeyword.value = dataSource?.getAttribute('data-active-keyword') || '';

    currentUserId.value = dataSource?.getAttribute('data-current-user-id') || '';

    canUseFulltextSearch.value = dataSource?.getAttribute('data-can-use-fulltext-search') === '1';

    folderCountLabelTemplate.value = dataSource?.getAttribute('data-folder-count-label') || '';
    fileCountLabelTemplate.value = dataSource?.getAttribute('data-file-count-label') || '';
    maxInputVars.value = Number(dataSource?.getAttribute('data-max-input-vars') || 0);
    bulkMaxItems.value = Number(dataSource?.getAttribute('data-bulk-max-items') || 0);

    const initialRestoreCounts = parseJsonAttribute(
      dataSource,
      'data-restore-counts'
    );
    if (initialRestoreCounts && typeof initialRestoreCounts === 'object') {
      const normalizeState = (state) => ({
        count: Number(state?.count || 0),
        display_ids: Array.isArray(state?.display_ids) ? state.display_ids : [],
        more: Number(state?.more || 0),
      });
      restoreCounts.value = {
        global: {
          restoring: normalizeState(initialRestoreCounts?.global?.restoring),
          restored: normalizeState(initialRestoreCounts?.global?.restored),
        },
        current: {
          restoring: normalizeState(initialRestoreCounts?.current?.restoring),
          restored: normalizeState(initialRestoreCounts?.current?.restored),
        },
      };
    }

    const resetFromServer =
      dataSource && dataSource.getAttribute('data-reset-localstorage') === '1';
    const resetFromUrl = (() => {
      try {
        const url = new URL(window.location.href);
        return url.searchParams.get('reset') === '1';
      } catch (error) {
        return false;
      }
    })();

    return {
      dataSource,
      shouldReset: resetFromServer || resetFromUrl,
    };
  };

  return {
    items,
    folders,
    allowedFolderIds,
    selectedFolderId,
    searchScope,
    hasSearchConditions,
    itemsTotal,
    folderSearchCount,
    focusQuery,
    activeKeyword,
    restoreCounts,
    currentUserId,
    canUseFulltextSearch,
    folderCountLabelTemplate,
    fileCountLabelTemplate,
    maxInputVars,
    bulkMaxItems,
    initDataSource,
  };
};
